/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-08
 */

// Add third-party dependencies.
import { useState, useCallback } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useSelector } from 'react-redux';

// Add local dependencies.
import AvatarUpload from './AvatarUpload';
import {
  useUploadAvatarMutation,
  useDeleteAvatarMutation,
} from '../../features/auth/authApi';
import type { RootState } from '../../store';
import { useSessionAuth } from '../../hooks/useSessionAuth';

/**
 * Props for the AvatarManager component.
 */
interface AvatarManagerProps {
  /**
   * Callback when avatar operations complete successfully.
   */
  onSuccess?: (message: string) => void;
  /**
   * Callback when avatar operations fail.
   */
  onError?: (error: string) => void;
  /**
   * Whether to show success/error messages inline.
   * @default true
   */
  showMessages?: boolean;
  /**
   * Maximum file size in bytes.
   * @default 5242880 (5MB)
   */
  maxFileSize?: number;
  /**
   * Allowed file types.
   * @default ['image/jpeg', 'image/png', 'image/webp']
   */
  allowedTypes?: string[];
}

/**
 * AvatarManager component that handles avatar upload, deletion, and state management.
 * Integrates with the backend API and manages loading states and error handling.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The avatar manager component
 *
 * @example
 * <AvatarManager
 *   onSuccess={(message) => showNotification(message)}
 *   onError={(error) => showError(error)}
 * />
 */
const AvatarManager: React.FC<AvatarManagerProps> = ({
  onSuccess,
  onError,
  showMessages = true,
  maxFileSize = 5242880, // 5MB
  allowedTypes = ['image/jpeg', 'image/png', 'image/webp'],
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const { checkSession } = useSessionAuth();

  // Local state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);

  // API mutations
  const [uploadAvatar, { isLoading: isUploading }] = useUploadAvatarMutation();
  const [deleteAvatar, { isLoading: isDeleting }] = useDeleteAvatarMutation();

  /**
   * Clears local messages.
   */
  const clearMessages = useCallback(() => {
    setLocalError(null);
    setLocalSuccess(null);
  }, []);

  /**
   * Handles file selection and upload.
   */
  const handleFileSelect = useCallback(
    async (file: File) => {
      clearMessages();
      setUploadProgress(0);

      try {
        // Simulate upload progress (since we don't have real progress from RTK Query)
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 10;
          });
        }, 100);

        const result = await uploadAvatar({ avatar: file }).unwrap();

        // Complete the progress
        clearInterval(progressInterval);
        setUploadProgress(100);

        // Update user data
        await checkSession();

        const successMessage =
          result.message || 'Avatar uploaded successfully!';
        setLocalSuccess(successMessage);
        onSuccess?.(successMessage);

        // Clear progress after a delay
        setTimeout(() => {
          setUploadProgress(0);
          setLocalSuccess(null);
        }, 2000);
      } catch (error: any) {
        setUploadProgress(0);

        let errorMessage = 'Failed to upload avatar. Please try again.';

        if (error?.data?.detail) {
          errorMessage = error.data.detail;
        } else if (error?.data?.error) {
          errorMessage = error.data.error;
        } else if (error?.data?.message) {
          errorMessage = error.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        setLocalError(errorMessage);
        onError?.(errorMessage);
      }
    },
    [uploadAvatar, checkSession, onSuccess, onError, clearMessages]
  );

  /**
   * Handles avatar deletion.
   */
  const handleDelete = useCallback(async () => {
    clearMessages();

    try {
      const result = await deleteAvatar().unwrap();

      // Update user data
      await checkSession();

      const successMessage = result.message || 'Avatar deleted successfully!';
      setLocalSuccess(successMessage);
      onSuccess?.(successMessage);

      // Clear success message after a delay
      setTimeout(() => {
        setLocalSuccess(null);
      }, 2000);
    } catch (error: any) {
      let errorMessage = 'Failed to delete avatar. Please try again.';

      if (error?.data?.detail) {
        errorMessage = error.data.detail;
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setLocalError(errorMessage);
      onError?.(errorMessage);
    }
  }, [deleteAvatar, checkSession, onSuccess, onError, clearMessages]);

  /**
   * Handles upload cancellation.
   */
  const handleCancel = useCallback(() => {
    setUploadProgress(0);
    clearMessages();
  }, [clearMessages]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
        Profile Photo
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center', mb: 3 }}
      >
        Upload a photo to personalize your profile
      </Typography>

      {/* Avatar Upload Component */}
      <AvatarUpload
        user={user}
        uploading={isUploading}
        uploadProgress={uploadProgress}
        error={showMessages ? localError || undefined : undefined}
        success={showMessages ? localSuccess || undefined : undefined}
        onFileSelect={handleFileSelect}
        onCancel={handleCancel}
        onDelete={handleDelete}
        allowDelete={!isDeleting}
        maxFileSize={maxFileSize}
        allowedTypes={allowedTypes}
      />

      {/* Additional Messages */}
      {showMessages && isDeleting && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Deleting avatar...
        </Alert>
      )}
    </Box>
  );
};

export default AvatarManager;
